import { useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash-es';
import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import type { Observable } from 'rxjs';
import { type Subscription, filter, fromEvent, map, of, tap } from 'rxjs';

import { useFlag } from '@/package';
import {
  useAuthStore,
  useCollectionManagementStore,
  useTenantStore,
} from '@/store';

// import mockMiriAlarmLog from './__tests__/mockMiriAlarmLog';
import WebSocketContext from './context';
import {
  onMiriDetroitModelTrainingBaseHandler,
  onMiriFrameBaseHandler,
  onMiriFrameBulkUpdateBaseHandler,
  onMiriTaskBaseHandler,
} from './event';
import type {
  WebSocketEventType,
  WebSocketMessage,
  WebSocketPayload,
} from './type';

const WebSocketProvider = ({ children }: PropsWithChildren) => {
  const queryClient = useQueryClient();
  const ws = useRef<{
    socket: WebSocket;
    open$: Observable<Event>;
    pingPong$: Observable<MessageEvent<'ping' | 'pong'>>;
    message$: Observable<Nullish<WebSocketMessage>>;
    subscriptionList: Subscription[];
  }>(null);
  const { currentTenantID, tenantList } = useTenantStore();
  const { user, getTokenInLocalStorage } = useAuthStore();
  const { readDirectory } = useCollectionManagementStore();
  const {
    value: isConnected,
    setFalse: didClose,
    setTrue: didOpen,
  } = useFlag(false);
  // eslint-disable-next-line react-hooks/refs
  const message$ = useMemo(() => ws.current?.message$, [isConnected]);
  const alarmMessage$ = useMemo(
    () =>
      // eslint-disable-next-line react-hooks/refs
      message$
        ? // eslint-disable-next-line react-hooks/refs
          message$.pipe(
            filter(event => !!event),
            filter(event => event.eventType === 'MiriAlarmLog'),
            map(event => event.payload as WebSocketPayload<'MiriAlarmLog'>),
          )
        : of(),
    [isConnected, message$],
  );

  const handleMessage = useCallback((message: WebSocketMessage) => {
    switch (message.eventType) {
      case 'MiriTask':
      case 'MiriAlarmLog':
        onMiriTaskBaseHandler(message as any, queryClient);
        break;
      case 'MiriFrame':
        onMiriFrameBaseHandler(message, queryClient);
        break;
      case 'MiriBulkUpdateFrameState':
        onMiriFrameBulkUpdateBaseHandler(message, queryClient);
        break;
      case 'MiriExportLabel':
        readDirectory({ childrenOption: { autoAdd: true, replace: true } });
        break;
      case 'MiriDetroitModelTraining':
        onMiriDetroitModelTrainingBaseHandler(message, queryClient);
        break;
    }
  }, []);

  const assert = () => {
    if (ws.current == null) return false;

    return true;
  };

  const closeWebSocket = () => {
    if (assert()) {
      ws.current!.subscriptionList.forEach(sub => sub.unsubscribe());
      ws.current!.socket.close();
      ws.current = null;
    }

    didClose();
  };

  const isEventType = (eventType: WebSocketEventType) => (value: any) =>
    eventType === value;

  const isExportLabelEventType = isEventType('MiriExportLabel');
  const isFrameEventType = isEventType('MiriFrame');
  const isTaskEventType = isEventType('MiriTask');

  const openWebSocket = useCallback(async () => {
    closeWebSocket();

    const idToken = getTokenInLocalStorage().idToken;

    if (!currentTenantID || !user.id || user.id === '-1' || !idToken) {
      console.warn('Must need tenantId');
      return;
    }

    const tenant = tenantList.find(v => v.tenantID === currentTenantID)!;
    const accessPath = tenant.accessPath;

    const socket = new WebSocket(
      `${import.meta.env.VITE_APP_WS_URL.replace(/http(s?):\/\//, token => `${token}${tenant.region}-`)}?tier=${accessPath}&tenantID=${currentTenantID}&userID=${user.id}&token=${idToken}`,
    );

    const open$ = fromEvent<Event>(socket, 'open').pipe(
      filter(() => socket.readyState === WebSocket.OPEN),
      tap(didOpen),
    );

    const pingPong$ = fromEvent<MessageEvent<'ping' | 'pong'>>(
      socket,
      'message',
    ).pipe(
      filter(e => e.data === 'ping'),
      tap(() => debounce(socket.send).bind(socket)('pong')),
    );

    const message$ = fromEvent<MessageEvent<string>>(socket, 'message').pipe(
      filter(e => e.data !== 'ping' && e.data !== 'pong'),
      map(e => {
        try {
          return JSON.parse(e.data) as WebSocketMessage;
        } catch {
          return null;
        }
      }),
    );

    ws.current = {
      socket,
      open$,
      pingPong$,
      message$,
      subscriptionList: [
        open$.subscribe(),
        pingPong$.subscribe(),
        message$
          .pipe(
            filter(message => !!message),
            tap(handleMessage),
          )
          .subscribe(),
      ],
    };
  }, [currentTenantID, handleMessage, user.id]);

  useEffect(() => {
    openWebSocket();

    return () => {
      closeWebSocket();
    };
  }, [openWebSocket, currentTenantID]);

  useEffect(() => {
    const interval = setInterval(async () => {
      if (!ws.current || ws.current.socket.readyState !== WebSocket.CLOSED) {
        return;
      }

      openWebSocket();
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [openWebSocket, currentTenantID]);

  useEffect(() => {
    return () => {
      closeWebSocket();
    };
  }, []);

  return (
    <WebSocketContext.Provider
      // eslint-disable-next-line react-hooks/refs
      value={{
        alarmMessage$,
        // eslint-disable-next-line react-hooks/refs
        message$,
        isConnected,
        isEventType,
        isExportLabelEventType,
        isFrameEventType,
        isTaskEventType,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketProvider;
